import { mdiClose, mdiSubdirectoryArrowRight } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { ISelectOption } from '../base';
import { AutomationRule, RuleMatch } from '../../services/worker-configuration/automation/worker-automation.types';
import {
    conditionMetaForScope,
    formatFilterGroupTitle,
    getConditionKindsForScope,
    getScopeDescription,
    getScopeIcon,
    getScopeSelectLabel,
} from '../../services/worker-configuration/automation/worker-automation-scope.registry';
import { WorkerStore } from '../../stores/worker-configuration/worker.store';
import { ConfigurationRuleConditionEditor } from './configuration-rule-condition-editor';
import { ConfigurationRulePayerEditor } from './configuration-rule-payer-editor';
import { getSingleSelectOption } from './configuration-base-components';
import * as S from './configuration-rule.styles';

interface IConfigurationRuleMatchBlockProps {
    availableScopes: string[];
    disabled?: boolean;
    match: RuleMatch;
    matchIndex: number;
    removable: boolean;
    rule: AutomationRule;
    store: WorkerStore;
}

export const ConfigurationRuleMatchBlock: React.FC<IConfigurationRuleMatchBlockProps> = observer(
    ({ availableScopes, disabled, match, matchIndex, removable, rule, store }) => {
        const kinds = getConditionKindsForScope(match.scope);
        const isPayerScope = match.scope === 'PAYER';
        const scopeOptions = useMemo<ISelectOption<string>[]>(
            () =>
                availableScopes.map(scope => ({
                    label: getScopeSelectLabel(scope),
                    value: scope,
                })),
            [availableScopes],
        );
        const selectedScope =
            scopeOptions.find(option => option.value === match.scope) ?? scopeOptions[0];

        return (
            <S.MatchBlock>
                <S.MatchBlockHeader>
                    <S.MatchBlockScopeIcon aria-hidden={true}>
                        <Icon path={getScopeIcon(match.scope)} size={0.85} />
                    </S.MatchBlockScopeIcon>

                    <S.MatchBlockScopeColumn>
                        <S.MatchBlockEyebrow>
                            Filter group · {formatFilterGroupTitle(matchIndex)}
                        </S.MatchBlockEyebrow>
                        <S.MatchBlockScopeSelect
                            options={scopeOptions}
                            value={selectedScope}
                            required
                            disabled={disabled}
                            onChange={selected => {
                                const option = getSingleSelectOption(selected);
                                store.setMatchBlockScope(
                                    rule.id,
                                    matchIndex,
                                    String(option?.value ?? match.scope),
                                );
                            }}
                        />
                    </S.MatchBlockScopeColumn>

                    <S.MatchBlockHeaderDescription>
                        {getScopeDescription(match.scope)}
                    </S.MatchBlockHeaderDescription>

                    {removable && (
                        <S.MatchBlockRemove
                            type="button"
                            disabled={disabled}
                            aria-label="Remove match condition"
                            onClick={() => store.removeMatchBlock(rule.id, matchIndex)}
                        >
                            <Icon path={mdiClose} size={0.7} aria-hidden={true} />
                        </S.MatchBlockRemove>
                    )}
                </S.MatchBlockHeader>

                <S.MatchBlockBody>
                    {!isPayerScope && (
                        <S.MatchConditionIntro>
                            <Icon path={mdiSubdirectoryArrowRight} size={0.65} aria-hidden={true} />
                            Conditions in this filter group
                        </S.MatchConditionIntro>
                    )}

                    <S.MatchConditionStack>
                        {isPayerScope ? (
                            <ConfigurationRulePayerEditor
                                ruleId={rule.id}
                                matchIndex={matchIndex}
                                payerIds={store.getConditionValuesForMatch(rule, matchIndex, 'payerId')}
                                store={store}
                                disabled={disabled}
                            />
                        ) : (
                            kinds.map((kind) => {
                                const meta = conditionMetaForScope(match.scope, kind);
                                return (
                                    <ConfigurationRuleConditionEditor
                                        key={kind}
                                        ruleId={rule.id}
                                        matchIndex={matchIndex}
                                        kind={kind}
                                        label={meta.label}
                                        hint={meta.hint}
                                        placeholder={
                                            kind === 'contains'
                                                ? 'Paste rejection message + Enter'
                                                : kind === 'carcGroup'
                                                  ? 'Search CARC groups…'
                                                  : kind === 'carcCode'
                                                    ? 'e.g. 261 + Enter'
                                                    : 'e.g. N687 + Enter'
                                        }
                                        values={store.getConditionValuesForMatch(rule, matchIndex, kind)}
                                        store={store}
                                        disabled={disabled}
                                    />
                                );
                            })
                        )}
                    </S.MatchConditionStack>
                </S.MatchBlockBody>
            </S.MatchBlock>
        );
    },
);
